import React, { useState } from 'react';
import { UsersIcon, PlusIcon, XMarkIcon, BuildingStorefrontIcon, CogIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '../../hooks';
import { businessAPI } from '../../services/api';
import api from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, Modal, Input, Select,
  LoadingPage, EmptyState, Avatar,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const SUB_ROLES = ['manager', 'inventory', 'support', 'finance'];
const SUB_ROLE_BADGE = { manager: 'purple', inventory: 'blue', support: 'green', finance: 'yellow' };

const InviteStaffModal = ({ businessId, open, onClose, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { sub_role: 'inventory' },
  });
  const { mutate: invite, loading } = useMutation(
    (d) => api.post(`/businesses/${businessId}/staff/invite`, d),
    { successMsg: 'Invitation sent!', onSuccess: () => { reset(); onSaved?.(); } }
  );
  return (
    <Modal open={open} onClose={onClose} title="Invite Staff Member"
      footer={<div className="flex justify-end gap-2"><Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button><Button variant="primary" size="sm" loading={loading} onClick={handleSubmit(invite)}>Send Invite</Button></div>}>
      <div className="space-y-4">
        <p className="text-sm text-surface-500">An invitation email will be sent with a link to join your business.</p>
        <Input label="Staff Email *" type="email" error={errors.email?.message}
          {...register('email', { required: 'Email required', pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' } })} />
        <Select label="Role" {...register('sub_role')}>
          {SUB_ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </Select>
        <div className="rounded-xl bg-surface-50 p-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-surface-400">Role Permissions</p>
          {[
            ['manager', 'Full business access except owner settings'],
            ['inventory', 'Add/edit products and update stock'],
            ['support', 'View orders and handle support chat'],
            ['finance', 'View orders, payments, and payouts'],
          ].map(([r, desc]) => (
            <div key={r} className="flex items-start gap-2 text-xs">
              <Badge variant={SUB_ROLE_BADGE[r]}>{r}</Badge>
              <span className="text-surface-500 pt-0.5">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export function StaffPage({ business }) {
  const [showInvite, setShowInvite] = useState(false);
  const { data, loading, refetch } = useQuery(
    () => api.get(`/businesses/${business?.id}/staff`).catch(() => ({ data: { data: [] } })),
    [business?.id],
    { enabled: !!business?.id }
  );
  const staff = Array.isArray(data) ? data : [];
  return (
    <div className="animate-in space-y-5">
      <SectionHeader
        title="Staff Management"
        subtitle="Manage your team and their permissions"
        action={<Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowInvite(true)}>Invite Staff</Button>}
      />

      {/* Role overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {SUB_ROLES.map(r => (
          <Card key={r} className="p-4 text-center">
            <Badge variant={SUB_ROLE_BADGE[r]} className="mb-2">{r}</Badge>
            <p className="text-xs text-surface-400 mt-1 capitalize">{r} access</p>
          </Card>
        ))}
      </div>

      <Card>
        {loading ? <LoadingPage /> : staff.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No staff yet"
            description="Invite team members to help manage your business"
            action={<Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowInvite(true)}>Invite First Staff</Button>}
          />
        ) : (
          <table className="data-table">
            <thead><tr><th>Staff Member</th><th>Role</th><th>Status</th><th>Joined</th><th></th></tr></thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <Avatar name={`${s.user?.first_name} ${s.user?.last_name}`} size="sm" />
                      <div>
                        <p className="font-medium text-surface-900">
                          {s.user ? `${s.user.first_name} ${s.user.last_name}` : s.email}
                        </p>
                        <p className="text-xs text-surface-400">{s.email}</p>
                      </div>
                    </div>
                  </td>
                  <td><Badge variant={SUB_ROLE_BADGE[s.sub_role] || 'gray'}>{s.sub_role}</Badge></td>
                  <td>
                    <Badge variant={s.accepted ? 'green' : 'yellow'}>
                      {s.accepted ? 'Active' : 'Pending'}
                    </Badge>
                  </td>
                  <td className="text-xs text-surface-400">{s.created_at ? format(new Date(s.created_at), 'dd MMM yy') : '—'}</td>
                  <td>

                    <button
                      className="btn btn-ghost p-1.5 rounded-lg text-danger-500"
                      onClick={() => {
                        api.delete(`/businesses/${business?.id}/staff/${s.id}`)
                          .then(() => refetch())
                      }}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <InviteStaffModal businessId={business?.id} open={showInvite} onClose={() => setShowInvite(false)} onSaved={() => { refetch(); setShowInvite(false); }} />
    </div>
  );
}

export function BusinessSettings({ business, onUpdate }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: business || {},
  });
  const { mutate: save, loading } = useMutation(
    (d) => businessAPI.update(business?.id, d),
    { successMsg: 'Business settings saved', onSuccess: onUpdate }
  );

  return (
    <div className="animate-in space-y-5 max-w-2xl">
      <SectionHeader title="Business Settings" subtitle="Manage your business profile and configuration" />

      <form onSubmit={handleSubmit(save)} className="space-y-5">
        {/* Basic info */}
        <Card>
          <h3 className="font-display font-semibold text-surface-900 mb-4">Business Profile</h3>
          <div className="space-y-4">

            <div className="grid grid-cols-2 gap-3">
              <Input label="Business Name *" error={errors.name?.message} {...register('name', { required: true })} />
              <Input label="Business Type" {...register('business_type')} placeholder="e.g. Retail, Electronics" />
              {/* <Input label="Return Policy (days)" type="number" {...register('return_policy_days')} /> */}
            </div>
            {/* <div><label className="label">Description</label>
              <textarea className="input" {...register('description')} rows={3} />
            </div> */}
          </div>
        </Card>

        {/* Tax / Legal */}
        <Card>
          <h3 className="font-display font-semibold text-surface-900 mb-4">Legal & Tax</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="GST Number" {...register('gst_number')} placeholder="22AAAAA0000A1Z5" readOnly
              className="bg-surface-100 cursor-not-allowed text-surface-500" />
            <Input label="PAN Number" {...register('pan_number')} placeholder="AAAAA1234A" readOnly
              className="bg-surface-100 cursor-not-allowed text-surface-500" />
          </div>
        </Card>

        {/* Address */}
        <Card>
          <h3 className="font-display font-semibold text-surface-900 mb-4">Business Address</h3>
          <div className="space-y-3">
            <Input label="Address Line 1" {...register('address_line1')} />
            <Input label="Address Line 2" {...register('address_line2')} />
            <div className="grid grid-cols-3 gap-3">
              <Input label="City" {...register('city')} />
              <Input label="State" {...register('state')} />
              <Input label="Pincode" {...register('pincode')} />
            </div>
          </div>
        </Card>

        {/* Contact */}
        <Card>
          <h3 className="font-display font-semibold text-surface-900 mb-4">Support Contact</h3>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Support Email" type="email" {...register('support_email')} />
            <Input label="Support Phone" type="tel" {...register('support_phone')} />
          </div>
        </Card>

        {/* Bank */}
        {/* <Card>
          <h3 className="font-display font-semibold text-surface-900 mb-4">Bank Account (for Payouts)</h3>
          <div className="space-y-3">
            <Input label="Account Holder Name" {...register('bank_account_name')} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Account Number" {...register('bank_account_number')} />
              <Input label="IFSC Code" {...register('bank_ifsc')} placeholder="e.g. SBIN0001234" />
            </div>
          </div>
          <p className="mt-3 text-xs text-surface-400">Bank details are used to link your Razorpay account for direct payouts.</p>
        </Card> */}

        <Button type="submit" variant="primary" size="lg" loading={loading} icon={CogIcon}>Save Settings</Button>
      </form>
    </div>
  );
}
